// Sitzung eines Mitarbeiters, der sich über einen Sammelaccount anmeldet.
//
// Ablauf: Das gemeinsame Microsoft-Konto öffnet AWARE (MSAL), danach fragt die
// Anmeldemaske nach der Mitarbeiternummer. Der Server gibt dafür ein
// eigenes Token aus (`api/_sharedMembers.js`), das ab dann bei jedem Request
// als `X-Aware-Member` mitläuft und die Person bestimmt.
//
// Bewusst `sessionStorage`: Die Sitzung gilt nur für dieses Fenster. Wer den
// Tab schließt oder das Gerät weitergibt, ist abgemeldet – an einem Terminal in
// der Halle ist das die richtige Voreinstellung.

import { directoryEmailFor } from "./directoryIdentity";

export type MemberSession = {
  token: string;
  email: string;
  username: string;
  displayName: string;
};

const KEY = "sherloxaware_member_session_v1";

let cached: MemberSession | null | undefined;
const listeners = new Set<() => void>();

function read(): MemberSession | null {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<MemberSession>;
    if (typeof parsed.token !== "string" || typeof parsed.email !== "string") return null;
    return {
      token: parsed.token,
      email: parsed.email.toLowerCase(),
      username: typeof parsed.username === "string" ? parsed.username : "",
      displayName: typeof parsed.displayName === "string" ? parsed.displayName : "",
    };
  } catch {
    return null;
  }
}

export function getMemberSession(): MemberSession | null {
  if (cached === undefined) cached = read();
  return cached;
}

function notify(): void {
  listeners.forEach((fn) => {
    try { fn(); } catch { /* ein kaputter Listener darf den Rest nicht aufhalten */ }
  });
}

export function setMemberSession(session: MemberSession): void {
  cached = session;
  try { sessionStorage.setItem(KEY, JSON.stringify(session)); } catch { /* Storage voll/gesperrt */ }
  notify();
}

export function clearMemberSession(): void {
  cached = null;
  try { sessionStorage.removeItem(KEY); } catch { /* ignore */ }
  notify();
}

export function subscribeMemberSession(fn: () => void): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

export function memberToken(): string | null {
  return getMemberSession()?.token ?? null;
}

/**
 * Die E-Mail-Adresse, unter der Fortschritt, Tests und Zertifikate laufen.
 * Ohne Sammelaccount ist das das Microsoft-Konto.
 */
export function identityEmail(fallback?: string | null): string {
  const session = getMemberSession();
  if (session) return session.email;
  // `fallback` ist der Entra-Anmeldename aus MSAL – maßgeblich ist die Adresse,
  // unter der das Verzeichnis die Person führt (siehe directoryIdentity.ts).
  return directoryEmailFor(fallback);
}

/** Anzeigename inklusive Sammelaccount-Fall (dort der selbst gesetzte Name). */
export function identityName(fallback?: string | null): string {
  const session = getMemberSession();
  if (session) return session.displayName || session.username;
  return fallback ?? "";
}

/**
 * Schlüssel für Level-Zuweisungen (`user_level_assignments`). Ohne Sammelaccount
 * ist das die Entra-Objekt-ID; ein Mitglied hat keine und nutzt seine eigene
 * Kennung – sonst hinge die ganze Schicht an der Zuweisung des gemeinsamen Kontos.
 */
export function identityUserId(fallback?: string | null): string {
  const session = getMemberSession();
  if (session) return session.email;
  return fallback ?? "";
}
